// =============================================================================
// app/src/lib/anexoPrevia.ts — a imagem do anexo, para MOSTRAR dentro da tela.
//
// O anexo da conta é JPEG ou PDF (044). O JPEG mostra-se direto. O PDF quase
// sempre é uma digitalização do iPhone: o iOS grava a folha como um JPEG CRU
// dentro do arquivo (/DCTDecode) e não põe texto nenhum — por isso o
// pdftotext nunca serviu para ler esses boletos. Dá para tirar a foto de
// dentro e mostrá-la como imagem, sem leitor de PDF, sem biblioteca e sem ida
// à rede. Medido nos 104 anexos do dono em 18/09/2026: todos assim; e nas 15
// notas de fornecedor de 25/09/2026, idem.
//
// PDF gerado por banco, que é texto de verdade, NÃO tem JPEG dentro: a função
// devolve nulo e a tela cai no caminho de sempre — abrir em outra aba.
//
// POR QUE data: E NÃO blob:. A CSP do sistema é `img-src 'self' data: <api>`
// (infra/Caddyfile). Um `<img src="blob:…">` é RECUSADO pelo navegador. O
// abrirComprovante() de lib/comprovante.ts pode usar blob porque ali é
// NAVEGAÇÃO para outra aba, não imagem desenhada na página.
// =============================================================================

/** Abaixo disto é miniatura, não a folha — o CamScanner e o iOS gravam uma
 *  prévia pequena junto. Mostrar a miniatura seria pior que não mostrar. */
export const MINIMO_DA_FOLHA = 8 * 1024;

/** Pedaço do `String.fromCharCode` — espalhar 500 mil argumentos de uma vez
 *  estoura a pilha do JavaScript. */
const PEDACO = 0x8000;

export function bytesDeBase64(base64: string): Uint8Array {
  const binario = atob(base64.replace(/\s/g, ''));
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

export function base64DeBytes(bytes: Uint8Array): string {
  let texto = '';
  for (let i = 0; i < bytes.length; i += PEDACO) {
    texto += String.fromCharCode(...bytes.subarray(i, i + PEDACO));
  }
  return btoa(texto);
}

/**
 * O maior JPEG dentro do arquivo, ou nulo.
 *
 * Varre o buffer inteiro procurando o começo (FF D8 FF) e o fim (FF D9) de
 * cada imagem. Fica com a MAIOR: um PDF de digitalização costuma trazer a
 * folha e uma miniatura, e a miniatura não serve para ler um boleto.
 */
export function jpegDentroDoPdf(bytes: Uint8Array): Uint8Array | null {
  let maior: Uint8Array | null = null;
  let i = 0;
  while (i < bytes.length - 3) {
    if (bytes[i] === 0xff && bytes[i + 1] === 0xd8 && bytes[i + 2] === 0xff) {
      let j = i + 3;
      while (j < bytes.length - 1 && !(bytes[j] === 0xff && bytes[j + 1] === 0xd9)) j += 1;
      if (j >= bytes.length - 1) break;      // começou e não terminou: arquivo cortado
      const achado = bytes.subarray(i, j + 2);
      if (!maior || achado.length > maior.length) maior = achado;
      i = j + 2;
      continue;
    }
    i += 1;
  }
  return maior && maior.length >= MINIMO_DA_FOLHA ? maior : null;
}

/**
 * O `src` de um `<img>` para este anexo, ou nulo quando não dá para mostrar.
 *
 * Nulo NÃO é erro: é "este arquivo não é uma foto de folha". A tela então
 * oferece abrir em outra aba, que funciona para qualquer PDF.
 */
export function previaDoAnexo(base64: string, contentType: string): string | null {
  const limpo = base64.replace(/\s/g, '');
  if (!limpo) return null;
  if (contentType === 'image/jpeg') return `data:image/jpeg;base64,${limpo}`;
  if (contentType !== 'application/pdf') return null;
  const jpeg = jpegDentroDoPdf(bytesDeBase64(limpo));
  return jpeg ? `data:image/jpeg;base64,${base64DeBytes(jpeg)}` : null;
}
